import React, { useEffect, useState, useSyncExternalStore } from 'react';
import {
  Alert, Button, Group, Loader, Modal, Select, Stack, Switch, Text,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import i18n from '../../../../i18n';
import { getTable } from '../../../../requests/documentDatas';
import {
  getTableCatalogSnapshot,
  getTableCatalogSource,
  isTemplateTableCatalog,
  loadTableCatalog,
  subscribeTableCatalog,
} from '../../../utils/tableCatalogRegistry';
import {
  blockColumnsOf, foldCanvasLabels, materializeTable,
} from '../../../utils/webTableMaterialize';
import {
  defaultTableConfig, isConfigurableTable, isSplittableTable,
} from '../../../../shared/tables/tableDescriptors';
import TableConfigScreen from './TableConfigScreen';
import TableRowsRangeField from './TableRowsRangeField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';

const NONE = '__none__';

/**
 * Une table se désigne par son nom **et** par le document qui la sert : une
 * offre propose ses propres tables et celles de chaque opportunité rattachée,
 * qui peuvent porter le même nom. La valeur du select encode les deux.
 */
function entryValue(entry) {
  return entry.source ? `${entry.source.type}:${entry.source.id}:${entry.name}` : entry.name;
}

function currentValue(tableName, tableSource) {
  if (!tableName) return NONE;
  return entryValue({ name: tableName, source: tableSource });
}

/**
 * Libellé humain d'une table, comme dans le Proposal classique
 * (`TableCascader` / `SelectTable` : `t(table)`) : l'API renvoie des clés
 * techniques (`OUTFITS_WORK_POSITIONS`), la table de traduction les rend
 * lisibles. i18n renvoie la clé elle-même quand la traduction manque.
 */
function tableLabel(name) {
  return i18n.t(name) || name;
}

/**
 * Champ « Variable tableau ».
 *
 * `tableName` n'a jamais été un titre : c'est la clé qui branche le bloc sur
 * une table du document, relue à chaque chargement du site
 * (`refreshVariableTablesDeep`). Un champ texte libre laissait une faute de
 * frappe débrancher le tableau en silence.
 *
 * Dans l'éditeur de modèle web, le modèle sert la liste de ses tables sans
 * données (`/web_templates/:id/tables`), comme un modèle classique : on y
 * branche le bloc par son nom, les lignes arrivent à la création du site
 * (`refreshVariableTablesDeep`).
 */
export default function TableBindingField({ value, onChange }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const { entries, splittableTables, status } = useSyncExternalStore(
    subscribeTableCatalog,
    getTableCatalogSnapshot,
  );
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  // Table choisie mais pas encore écrite dans la data : le select doit
  // l'afficher pendant la lecture de l'API, sinon il repasse sur l'ancienne.
  const [pendingName, setPendingName] = useState(null);
  // Table paramétrable tout juste branchée : son écran de configuration
  // s'ouvre dès qu'elle est dans la data, comme le Drawer du Proposal
  // (`NavbarTable` → `openTableConfigDrawer`). Ouvert après le montage de
  // l'écran, sinon il apparaît sans glisser.
  const [openConfigFor, setOpenConfigFor] = useState(null);
  // Table découpable en attente du choix « un par zone / un pour toutes ».
  const [splitChoiceFor, setSplitChoiceFor] = useState(null);

  // Chargement paresseux : la liste n'est lue qu'à la sélection d'un Tableau.
  useEffect(() => { loadTableCatalog(); }, []);

  useEffect(() => {
    if (!openConfigFor || openConfigFor !== value) return;
    setOpenConfigFor(null);
    setConfigOpen(true);
  }, [openConfigFor, value]);

  if (status === 'unavailable') return null;

  const isTemplate = isTemplateTableCatalog();

  const bound = typeof value === 'string' && value.trim() !== '';
  const tableSource = itemProps.tableSource || null;
  const selected = currentValue(bound ? value : '', tableSource);

  // Tri sur le libellé traduit, comme le cascader du Proposal classique :
  // l'ordre des clés techniques renvoyé par l'API n'a aucun sens à l'écran.
  // Les tables d'une opportunité rattachée sont groupées sous son nom, comme
  // les branches du cascader.
  const byGroup = new Map();
  entries.forEach((entry) => {
    const group = entry.group || '';
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push({ value: entryValue(entry), label: tableLabel(entry.name) });
  });
  byGroup.forEach((items) => items.sort((a, b) => a.label.localeCompare(b.label)));

  const options = [];
  const ownTables = byGroup.get('') || [];
  options.push(...ownTables);
  Array.from(byGroup.entries())
    .filter(([group]) => group !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([group, items]) => options.push({ group, items }));

  // Une table disparue du document reste proposée : sans ça, le select
  // afficherait vide et le premier clic ailleurs débrancherait le bloc.
  const known = entries.some((entry) => entryValue(entry) === selected);
  if (bound && !known) {
    options.unshift({ value: selected, label: `${tableLabel(value)} (introuvable)` });
  }

  /** Lignes brutes de la table, telles que l'API les renvoie. */
  /**
   * Contenu du bloc pour une table : colonnes et lignes matérialisées par le
   * moteur partagé avec le Proposal, filtrées par la configuration du bloc.
   */
  const fetchMaterialized = async (name, tableConfig, from, blockProps = {}) => {
    const source = from || getTableCatalogSource();
    if (!source || !name) return null;
    const table = await getTable(source, name, { split: !!blockProps.shouldSplit, tableConfig });
    // `name` : la table qu'on branche, pas forcément celle que le bloc porte encore.
    const materialized = materializeTable(table, tableConfig, { ...blockProps, tableName: name });
    if (!materialized) throw new Error('table illisible');
    return materialized;
  };

  /**
   * Rechargement d'une table déjà branchée : exactement ce que fait le
   * rafraîchissement des variables, le bouton ne fait qu'avancer l'échéance.
   * Colonnes **et** lignes sont recalculées — c'est la configuration qui dit
   * lesquelles paraissent, et sous quel nom.
   *
   * `patch` : nouvelle plage de lignes ou découpe, appliquée dans le même
   * dispatch que le contenu qu'elle produit.
   */
  const reloadRows = async (patch = {}) => {
    if (!bound) return;
    setReloading(true);
    setReloadError(null);
    try {
      // Comme au rafraîchissement : ce qui a été renommé au canvas passe dans
      // la configuration avant que le contenu soit recalculé.
      const tableConfig = foldCanvasLabels(itemProps.tableConfig || null, blockColumnsOf(itemProps));
      const blockProps = { ...itemProps, ...patch };
      const materialized = await fetchMaterialized(value, tableConfig, tableSource, blockProps);
      if (materialized) updateItemProps({ tableConfig, ...patch, ...materialized });
    } catch (e) {
      console.error('[webviewer] rechargement des lignes du tableau', value, e);
      setReloadError('Table illisible — les lignes actuelles sont conservées.');
    } finally {
      setReloading(false);
    }
  };

  /**
   * Choisir une table charge son contenu dans la foulée : sans ça, le bloc
   * restait sur ses anciennes lignes jusqu'au prochain chargement du site, ce
   * qui donnait l'impression que le branchement n'avait rien fait.
   *
   * Un branchement repart d'une configuration neuve : les overrides de
   * colonnes de la table précédente ne veulent plus rien dire.
   *
   * Nom, configuration, colonnes et lignes partent dans **un seul** dispatch :
   * `updateItemProps` repart des props du rendu courant, un `onChange` séparé
   * avant l'attente serait écrasé.
   *
   * Table découpable, dans un site : on demande d'abord « un par zone ou un
   * pour toutes », comme la modale du Proposal (`pendingSplitChoice`). Un
   * modèle la branche non découpée, le switch décide ensuite.
   */
  const selectTable = (next) => {
    if (next === NONE || next == null) {
      onChange('');
      return;
    }
    const entry = entries.find((candidate) => entryValue(candidate) === next);
    const name = entry ? entry.name : next;
    if (!isTemplate && isSplittableTable(splittableTables, name)) {
      setSplitChoiceFor(next);
      return;
    }
    bindTable(next, false);
  };

  const bindTable = async (next, shouldSplit) => {
    const entry = entries.find((candidate) => entryValue(candidate) === next);
    const name = entry ? entry.name : next;
    const nextSource = entry?.source || null;

    setPendingName(next);
    setReloading(true);
    setReloadError(null);
    const tableConfig = defaultTableConfig(name);
    // `tableSource` accompagne le nom : sans elle, une table d'opportunité
    // rattachée serait relue sur l'offre, qui ne la sert pas.
    // Une nouvelle table repart entière, comme une box neuve du Proposal.
    const binding = {
      tableName: name,
      tableConfig,
      tableSource: nextSource,
      rowsSelectionStart: null,
      rowsSelectionEnd: null,
      shouldSplit,
    };
    if (isConfigurableTable(name)) setOpenConfigFor(name);
    // Un modèle n'a pas de document : rien à lire, seul le branchement compte.
    if (isTemplate) {
      updateItemProps(binding);
      setReloading(false);
      setPendingName(null);
      return;
    }
    try {
      const materialized = await fetchMaterialized(name, tableConfig, nextSource, binding);
      // Table sans aucune ligne : rien à matérialiser, on branche sans toucher
      // au contenu plutôt que de vider le bloc.
      if (materialized) {
        updateItemProps({ ...binding, ...materialized });
      } else {
        updateItemProps(binding);
      }
    } catch (e) {
      console.error('[webviewer] lecture des lignes du tableau', name, e);
      // Le branchement est fait quand même : seul le contenu manque.
      updateItemProps(binding);
      setReloadError('Table illisible — les lignes actuelles sont conservées.');
    } finally {
      setReloading(false);
      setPendingName(null);
    }
  };

  const chooseSplit = (shouldSplit) => {
    const next = splitChoiceFor;
    setSplitChoiceFor(null);
    bindTable(next, shouldSplit);
  };

  /**
   * Découper ou recoller un tableau branché. Dans un modèle, seul le réglage
   * compte ; dans un site, le contenu est relu dans la forme demandée. La plage
   * de lignes repart de zéro : elle ne s'applique qu'à une table entière.
   */
  const toggleSplit = (shouldSplit) => {
    const patch = {
      shouldSplit,
      rowsSelectionStart: null,
      rowsSelectionEnd: null,
      ...(shouldSplit ? {} : { splitSlices: [] }),
    };
    if (isTemplate) {
      updateItemProps(patch);
      return;
    }
    reloadRows(patch);
  };

  return (
    <Stack gap={6}>
      <Select
        size="xs"
        label="Variable tableau"
        wrapperProps={{ 'data-inspector-layout': 'stacked' }}
        data={[{ value: NONE, label: 'Aucune (tableau libre)' }, ...options]}
        value={pendingName ?? selected}
        onChange={selectTable}
        disabled={status === 'loading' || reloading}
        rightSection={
          status === 'loading' || reloading ? <Loader size="xs" /> : undefined
        }
        comboboxProps={{ withinPortal: true }}
      />
      {status === 'error' && (
        <Text size="xs" c="red">
          Liste des tables indisponible. Réessayez plus tard.
        </Text>
      )}
      {bound && (
        <Alert
          variant="light"
          color="editor"
          icon={<FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'light' })} style={{ fontSize: 16 }} />}
          p="xs"
        >
          <Text size="xs">
            {isTemplate
              ? 'Les lignes de ce tableau seront lues dans le document à la création du site.'
              : 'Le contenu de ce tableau vient de la table du document : il se met à jour tout seul et ne s’édite pas à la main.'}
          </Text>
          <Group gap="xs" mt={6}>
            {/* Toute table branchée a des colonnes et une fusion à régler ; une
                table paramétrable a en plus ses axes. */}
            {!!value && (
              <Button
                size="compact-xs"
                variant="filled"
                color="editor"
                leftSection={<FontAwesomeIcon icon={icon({ name: 'gear', style: 'light' })} style={{ fontSize: 14 }} />}
                onClick={() => setConfigOpen(true)}
              >
                Configurer le tableau
              </Button>
            )}
            {!isTemplate && (
              <Button
                size="compact-xs"
                variant="light"
                color="editor"
                leftSection={<FontAwesomeIcon icon={icon({ name: 'arrows-rotate', style: 'light' })} style={{ fontSize: 14 }} />}
                loading={reloading}
                onClick={() => reloadRows()}
              >
                Recharger maintenant
              </Button>
            )}
          </Group>
          {reloadError && <Text size="xs" c="red" mt={4}>{reloadError}</Text>}
        </Alert>
      )}
      {bound && isSplittableTable(splittableTables, value) && (
        <Switch
          size="sm"
          label={i18n.t('table-split-modal-split')}
          checked={!!itemProps.shouldSplit}
          disabled={reloading}
          onChange={(e) => toggleSplit(e.currentTarget.checked)}
        />
      )}
      {/* Une plage de lignes par bloc : elle ne vaut que pour une table entière
          — découpé, chaque slice est une entité à part. */}
      {bound && !isTemplate && !itemProps.shouldSplit && (
        <TableRowsRangeField disabled={reloading} onChange={reloadRows} />
      )}
      <Modal
        opened={!!splitChoiceFor}
        onClose={() => setSplitChoiceFor(null)}
        centered
        withCloseButton={false}
      >
        <Text size="sm">{i18n.t('table-split-modal-question')}</Text>
        <Group justify="flex-end" gap="xs" mt="md">
          <Button variant="default" size="xs" onClick={() => setSplitChoiceFor(null)}>
            {i18n.t('cancel')}
          </Button>
          <Button size="xs" color="editor" onClick={() => chooseSplit(false)}>
            {i18n.t('table-split-modal-non-split')}
          </Button>
          <Button size="xs" color="editor" onClick={() => chooseSplit(true)}>
            {i18n.t('table-split-modal-split')}
          </Button>
        </Group>
      </Modal>
      {/* Monté en permanence dès qu'une table est branchée : c'est ce qui
          permet au panneau de **glisser** à l'ouverture comme à la fermeture.
          Monté à la demande, il apparaissait d'un coup. */}
      {bound && !!value && (
        // Remonté à chaque table : sa configuration de départ est celle de la
        // table branchée, pas celle de la précédente.
        <TableConfigScreen key={value} opened={configOpen} onClose={() => setConfigOpen(false)} />
      )}
    </Stack>
  );
}
